/**
 * StockForge Web - API Service Layer
 * HTTP client with auth injection, error handling, and request management
 *
 * Security Features:
 * - HTTPS enforcement in production
 * - Auth token injection via interceptors
 * - Automatic token refresh on 401
 * - Request/response sanitization
 * - Rate limiting awareness
 */

import { useAuthStore } from '@/store/authStore';
import { getSessionCacheEpoch } from '@/lib/sessionCache';
import { logger } from '@/lib/logger';
import { ENDPOINTS } from '@shared/constants';
import type {
  CreateImportJobRequest,
  DirectImportPreviewProviderKey,
  EcommerceImportSource,
  ImportBulkConflictActionResult,
  ImportHeaderMapping,
  ImportJobCollectionResponse,
  ImportJobResponse,
  ImportJobMode,
  ImportProviderMetadata,
  ImportRowAction,
} from '@shared/importContracts/inventoryImportJob';
import { createPurchaseOrderTransport } from '@/services/purchaseOrderWorkflow';
import type { FulfillmentStepResult } from '@/services/fulfillmentLifecycle';
import type {
ApiError,
RecipeSizeFamily,
SizeVariant,
User,
} from '@shared/types';
import type { ApiEnvelope, BuildPlanStatus } from '@shared/types/apiContracts';

export type { ApiEnvelope };
import type {
AnomalySeverity,
AnomalySummary,
AnomalyType,
DetectedAnomaly,
} from '@shared/types/anomaly';
import axios,{
type AxiosError,
type AxiosInstance,
type AxiosResponse,
type InternalAxiosRequestConfig,
} from 'axios';

// ============================================
// Security Configuration
// ============================================

/**
 * Validate and sanitize the API base URL
 * Enforces HTTPS in production environments
 */
const getSecureApiUrl = (): string => {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const isProduction = import.meta.env.PROD;

  if (!apiUrl) {
    return ''; // Empty URL uses relative paths (same origin)
  }

  try {
    const url = new URL(apiUrl);

    // Enforce HTTPS in production
    if (isProduction && url.protocol !== 'https:') {
      logger.error('Security Warning: API URL must use HTTPS in production');
      // Upgrade to HTTPS
      url.protocol = 'https:';
    }

    // Prevent open redirect vulnerabilities - only allow specific domains
    const allowedDomains = [
      'localhost',
      '127.0.0.1',
      'api.stockforge.ai',
      'app.stockforge.ai',
    ];

    const hostname = url.hostname.toLowerCase();
    if (!allowedDomains.some(domain => hostname === domain || hostname.endsWith('.' + domain))) {
      logger.error('Security Warning: API URL domain not in allowlist');
      return ''; // Fall back to same-origin
    }

    return url.toString().replace(/\/$/, ''); // Remove trailing slash
  } catch {
    logger.error('Invalid API URL configuration');
    return '';
  }
};

// ============================================
// Types
// ============================================

interface RequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _skipAuth?: boolean;
  _requestId?: string;
  _sessionEpoch?: number;
}

type CurrentUserResponse = {
  user: User;
};

const AUTH_ENDPOINTS = new Set<string>([
  ENDPOINTS.auth.login,
  ENDPOINTS.auth.register,
  ENDPOINTS.auth.refresh,
  ENDPOINTS.auth.forgotPassword,
  ENDPOINTS.auth.resetPassword,
  ENDPOINTS.auth.otpRequest,
  ENDPOINTS.auth.otpVerify,
  ENDPOINTS.auth.magicLinkRequest,
  ENDPOINTS.auth.magicLinkVerify,
  ENDPOINTS.auth.passkeyAuthOptions,
  ENDPOINTS.auth.passkeyAuthVerify,
  ENDPOINTS.auth.verifyEmail,
]);

const isAuthEndpoint = (url: string): boolean => {
  if (!url) return false;
  return [...AUTH_ENDPOINTS].some((endpoint) =>
    url === endpoint || url.endsWith(endpoint)
  );
};

// ============================================
// API Error Class
// ============================================

export class ApiRequestError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly details?: Record<string, unknown>;
  public readonly validationErrors?: ApiError['validationErrors'];

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.code = error.code;
    this.statusCode = error.statusCode;
    this.details = error.details;
    this.validationErrors = error.validationErrors;
  }
}

// ============================================
// API Client Class
// ============================================

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: getSecureApiUrl(),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      // Send the HttpOnly refresh cookie on same-origin/allowlisted requests.
      // The cookie is path-scoped to /api/v1/auth so it only accompanies
      // auth calls; CSRF on /auth/refresh is blocked by SameSite=Strict.
      withCredentials: true,
      xsrfCookieName: 'XSRF-TOKEN', // CSRF cookie name
      xsrfHeaderName: 'X-XSRF-TOKEN', // CSRF header name
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token
    this.client.interceptors.request.use(
      async (requestConfig: RequestConfig) => {
        if (requestConfig._skipAuth) {
          return requestConfig;
        }

        if (requestConfig._sessionEpoch !== undefined && requestConfig._sessionEpoch !== getSessionCacheEpoch()) {
          return Promise.reject(new Error('Session changed'));
        }
        requestConfig._sessionEpoch = getSessionCacheEpoch();

        const { accessToken } = useAuthStore.getState();
        if (accessToken) {
          requestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }

        return requestConfig;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Handle errors and token refresh
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        const requestConfig = response.config as RequestConfig;
        if (requestConfig._sessionEpoch !== undefined && requestConfig._sessionEpoch !== getSessionCacheEpoch()) {
          return Promise.reject(new Error('Session changed'));
        }
        return response;
      },
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as RequestConfig;
        const requestUrl = originalRequest?.url ?? '';
        const isAuthRequest = isAuthEndpoint(requestUrl);

        // Handle 401 - Token expired
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
          if (originalRequest._sessionEpoch !== getSessionCacheEpoch()) {
            throw error;
          }
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.refreshSubscribers.push({
                resolve: (token: string) => {
                  if (originalRequest._sessionEpoch !== getSessionCacheEpoch()) {
                    reject(error);
                    return;
                  }
                  originalRequest.headers = originalRequest.headers ?? {};
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(this.client(originalRequest));
                },
                reject,
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newToken = await this.refreshToken();
            if (originalRequest._sessionEpoch !== getSessionCacheEpoch()) {
              throw error;
            }
            this.onRefreshed(newToken);
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.onRefreshFailed(refreshError);
            if (originalRequest._sessionEpoch === getSessionCacheEpoch()) {
              useAuthStore.getState().logout();
            }
            throw refreshError;
          } finally {
            this.isRefreshing = false;
          }
        }

        if (!error.response) {
          const timedOut = error.code === 'ECONNABORTED';
          throw new ApiRequestError({
            message: timedOut
              ? 'Import is taking too long. Try a smaller file or wait a moment and retry.'
              : 'Could not reach the server. Check your connection and try again.',
            code: timedOut ? 'REQUEST_TIMEOUT' : 'NETWORK_ERROR',
            statusCode: timedOut ? 408 : 0,
            details: { axiosCode: error.code ?? 'NO_RESPONSE' },
          });
        }

        // Transform error response — backend wraps errors in { success, error: { code, message } }
        // Blob responses (responseType: 'blob') hold that envelope as text, so
        // read it back or every download failure reports axios's generic message.
        let rawData = error.response?.data as unknown;
        if (rawData instanceof Blob) {
          try {
            rawData = JSON.parse(await rawData.text());
          } catch {
            rawData = undefined;
          }
        }
        const responseData = rawData as Record<string, unknown> | undefined;
        const nestedError = responseData?.error as Partial<ApiError> | undefined;
        const apiError: ApiError = {
          message: nestedError?.message || (responseData?.message as string) || error.message || 'An unexpected error occurred',
          code: nestedError?.code || (responseData?.code as string) || 'UNKNOWN_ERROR',
          statusCode: error.response?.status ?? 500,
          details: nestedError?.details,
          validationErrors: nestedError?.validationErrors,
        };

        throw new ApiRequestError(apiError);
      }
    );
  }

  /**
   * Exchange the HttpOnly refresh cookie for a new access token.
   *
   * The backend rotates the refresh token on every use and treats reuse of
   * a rotated token as theft (revoking all sessions), so concurrent
   * refreshes from multiple tabs sharing the cookie must be serialized.
   * The Web Locks API provides a cross-tab mutex; each tab's request then
   * carries the freshest cookie.
   */
  private async refreshToken(): Promise<string> {
    const doRefresh = async (): Promise<string> => {
      const epoch = getSessionCacheEpoch();
      const response = await this.client.post<{
        message: string;
        tokens: {
          accessToken: string;
          refreshToken: string;
        };
      }>(
        ENDPOINTS.auth.refresh,
        // No body token: the HttpOnly cookie authenticates this request and
        // is rotated by the server. The body refreshToken in the response is
        // for native clients and is ignored here.
        {},
        { _skipAuth: true } as RequestConfig
      );

      if (epoch !== getSessionCacheEpoch()) {
        throw new Error('Session changed');
      }
      const { accessToken } = response.data.tokens;
      useAuthStore.getState().updateAccessToken(accessToken);
      return accessToken;
    };

    if (typeof navigator !== 'undefined' && 'locks' in navigator) {
      return navigator.locks.request('stockforge-token-refresh', doRefresh);
    }
    return doRefresh();
  }

  /**
   * Restore the session after a page load: trade the refresh cookie for an
   * access token. Returns true on success. On failure, clears this tab's
   * auth state (without broadcasting logout to healthy tabs).
   */
  async restoreSession(): Promise<boolean> {
    const epoch = getSessionCacheEpoch();
    try {
      const accessToken = await this.refreshToken();
      if (epoch !== getSessionCacheEpoch()) {
        return false;
      }
      const response = await this.client.get<CurrentUserResponse>(ENDPOINTS.auth.me);
      if (epoch !== getSessionCacheEpoch()) {
        return false;
      }
      useAuthStore.getState().login(response.data.user, accessToken);
      return true;
    } catch (error) {
      if (epoch !== getSessionCacheEpoch()) {
        return false;
      }
      logger.error('Session restore failed', error);
      useAuthStore.getState().clearLocalSession();
      return false;
    }
  }

  private onRefreshed(token: string): void {
    this.refreshSubscribers.forEach((subscriber) => subscriber.resolve(token));
    this.refreshSubscribers = [];
  }

  private onRefreshFailed(error: unknown): void {
    this.refreshSubscribers.forEach((subscriber) => subscriber.reject(error));
    this.refreshSubscribers = [];
  }

  // ============================================
  // HTTP Methods
  // ============================================

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: unknown, options?: { timeout?: number }): Promise<T> {
    const response = await this.client.post<T>(url, data, {
      timeout: options?.timeout,
    });
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  async postForm<T>(url: string, form: FormData, options?: { timeout?: number }): Promise<T> {
    const response = await this.client.post<T>(url, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: options?.timeout,
    });
    return response.data;
  }

  async getBlob(url: string, params?: Record<string, unknown>): Promise<Blob> {
    const response = await this.client.get<Blob>(url, {
      params,
      responseType: 'blob',
    });
    return response.data;
  }
}

// ============================================
// Singleton Export
// ============================================

export const api = new ApiClient();
const purchaseOrderTransport = createPurchaseOrderTransport(api);

const IMPORT_JOB_REQUEST_TIMEOUT_MS = 120000;

export interface ImportPreflightSheet {
  key: 'materials' | 'vendors' | 'warehouses' | 'inventoryLocations' | 'lots' | 'serials' | 'recipes' | 'recipeComponents';
  label: string;
  headers: string[];
  rowCount: number;
  missingRequiredColumns: string[];
  headerMapping: ImportHeaderMapping[];
}

export interface ImportPreflightResult {
  fileType: 'CSV' | 'XLSX';
  sheetNames: string[];
  totalRows: number;
  issues: string[];
  warnings: string[];
  sheets: ImportPreflightSheet[];
}

export interface ImportAiConversionMapping {
  sourceHeader: string;
  targetHeader: string;
  confidence: number;
  reason?: string;
}

export interface ImportAiConversionResult {
  fileName: string;
  csvContent: string;
  sheetName: string;
  rowCount: number;
  mappings: ImportAiConversionMapping[];
  unmappedRequiredColumns: string[];
  warnings: string[];
}

export interface ApiListEnvelope<T> extends ApiEnvelope<T[]> {
  count: number;
}

function unwrapApiEnvelope<T>(response: ApiEnvelope<T>, fallbackMessage: string): T {
  if (response.success === false || response.data === undefined) {
    throw new ApiRequestError({
      message: response.message || fallbackMessage,
      code: 'API_ENVELOPE_ERROR',
      statusCode: 200,
    });
  }

  return response.data;
}

// ============================================
// Recipe BOM Import (one-shot /recipes/import/bom)
// ============================================

export type RecipeImportSheet = 'Recipes' | 'BOM';
export type RecipeImportSeverity = 'error' | 'warning';
export type RecipeImportAction = 'create' | 'update' | 'skip';

export interface RecipeImportIssue {
  sheet: RecipeImportSheet;
  rowNumber: number;
  code: string;
  message: string;
  severity?: RecipeImportSeverity;
  recipeSku?: string;
  componentSku?: string;
}

export interface RecipeImportPlannedRecipe {
  recipeSku: string;
  action: RecipeImportAction;
  componentCount: number;
  /** Item this recipe produces, when the importer could resolve one. */
  outputSku?: string;
  /** Component rows were dropped because their SKU resolved to nothing. */
  incomplete?: boolean;
}

export interface RecipeImportResult {
  dryRun: boolean;
  totalRecipes: number;
  validRecipes: number;
  created: number;
  updated: number;
  skipped: number;
  errors: RecipeImportIssue[];
  warnings: RecipeImportIssue[];
  droppedComponents: number;
  incompleteRecipes: string[];
  recipesWithoutOutput: number;
  recipes: RecipeImportPlannedRecipe[];
}


export type KitImportSheet = 'Kits' | 'Components';

export interface KitImportIssue {
  sheet: KitImportSheet;
  rowNumber: number;
  code: string;
  message: string;
  severity?: 'error' | 'warning';
  kitSku?: string;
  componentSku?: string;
}

export interface KitImportPlannedKit {
  kitSku: string;
  action: 'create' | 'update' | 'skip';
  componentCount: number;
  incomplete?: boolean;
}

export interface KitImportResult {
  dryRun: boolean;
  totalKits: number;
  validKits: number;
  created: number;
  updated: number;
  skipped: number;
  errors: KitImportIssue[];
  warnings: KitImportIssue[];
  droppedComponents: number;
  incompleteKits: string[];
  kits: KitImportPlannedKit[];
}

export type ReferenceImageAngle = 'front' | 'back' | 'side' | 'top' | 'bottom' | 'detail' | 'angle';

export interface ReferenceImageRecord {
  id: string;
  recipeId: string;
  imagePath?: string;
  imageUri: string;
  thumbnailUri: string;
  angle?: ReferenceImageAngle | null;
  features?: string | null;
  createdAt: string;
  recipe?: {
    id: string;
    name: string;
    organizationId?: string | null;
  } | null;
}

export interface ReferenceImageUploadInput {
  image: File;
  recipeId: string;
  angle?: ReferenceImageAngle;
  features?: string;
}

export interface PaginatedApiEnvelope<T> extends ApiEnvelope<T> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AnomalyListParams extends Record<string, unknown> {
  type?: AnomalyType;
  severity?: AnomalySeverity;
  resolved?: boolean;
  materialId?: string;
}

// ============================================
// Typed API Methods for Resources
// ============================================

export const apiMethods = {
  // Items
  items: {
    list: (params?: Record<string, unknown>) => api.get(ENDPOINTS.items.list, params),
    get: (id: string) => api.get(ENDPOINTS.items.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.items.create, data),
    update: (id: string, data: unknown) => api.patch(ENDPOINTS.items.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.items.delete(id)),
    bulkDelete: (ids: string[]) => api.post(ENDPOINTS.items.bulkDelete, { ids }),
    bulkLowStockEmailAlerts: (ids: string[], enabled: boolean) =>
      api.post(ENDPOINTS.items.bulkLowStockEmailAlerts, { ids, enabled }),
    bulkThresholds: (data: unknown) => api.post(ENDPOINTS.items.bulkThresholds, data),
    bulkMetadata: (data: unknown) => api.post(ENDPOINTS.items.bulkMetadata, data),
    adjustStock: (id: string, data: unknown) => api.post(ENDPOINTS.items.adjustStock(id), data),
    getLowStock: () => api.get(ENDPOINTS.items.lowStock),
    getBySku: (sku: string) => api.get(ENDPOINTS.items.bySku(sku)),
    getByBarcode: (barcode: string) => api.get(ENDPOINTS.items.byBarcode(barcode)),
  },

  // Deprecated Material compatibility API
  materials: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.materials.list, params),
    get: (id: string) => api.get(ENDPOINTS.materials.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.materials.create, data),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.materials.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.materials.delete(id)),
    bulkDelete: (ids: string[]) => api.post(ENDPOINTS.materials.bulkDelete, { ids }),
    bulkLowStockEmailAlerts: (ids: string[], enabled: boolean) =>
      api.post(ENDPOINTS.materials.bulkLowStockEmailAlerts, { ids, enabled }),
    bulkThresholds: (data: unknown) => api.post(ENDPOINTS.materials.bulkThresholds, data),
    bulkMetadata: (data: unknown) => api.post(ENDPOINTS.materials.bulkMetadata, data),
    adjustStock: (id: string, data: unknown) =>
      api.post(ENDPOINTS.materials.adjustStock(id), data),
    getLowStock: () => api.get(ENDPOINTS.materials.lowStock),
    getBySku: (sku: string) => api.get(ENDPOINTS.materials.bySku(sku)),
    getByBarcode: (barcode: string) =>
      api.get(ENDPOINTS.materials.byBarcode(barcode)),
  },

  // Recipes
  recipes: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.recipes.list, params),
    get: (id: string) => api.get(ENDPOINTS.recipes.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.recipes.create, data),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.recipes.update(id), data),
    replaceComponents: (id: string, components: unknown[]) =>
      api.put(`${ENDPOINTS.recipes.get(id)}/components`, { components }),
    delete: (id: string) => api.delete(ENDPOINTS.recipes.delete(id)),
    importBomWorkbook: (
      file: File,
      options?: { dryRun?: boolean; skipUnresolved?: boolean }
    ) => {
      const form = new FormData();
      form.append('file', file);
      const dryRun = options?.dryRun ?? true;
      const skipUnresolved = options?.skipUnresolved === true;
      return api.postForm<ApiEnvelope<RecipeImportResult>>(
        `/api/v1/recipes/import/bom?dryRun=${dryRun ? 'true' : 'false'}`
          + `&skipUnresolved=${skipUnresolved ? 'true' : 'false'}`,
        form,
        { timeout: 120000 }
      );
    },
    exportBom: (params?: { format?: 'xlsx' | 'csv'; search?: string; isActive?: boolean }) =>
      api.getBlob('/api/v1/recipes/export/bom', {
        format: params?.format ?? 'xlsx',
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.isActive === undefined ? {} : { isActive: params.isActive }),
      }),
    getBySku: (sku: string) => api.get(ENDPOINTS.recipes.bySku(sku)),
    getVersions: (id: string) => api.get(ENDPOINTS.recipes.versions(id)),
    getPdf: (id: string) => api.getBlob(`/api/v1/recipes/${id}/pdf`),
    getBomTree: (id: string, quantity?: number) =>
      api.get(ENDPOINTS.recipes.bomTree(id), quantity ? { quantity } : undefined),
    getBomFlat: (id: string, quantity?: number) =>
      api.get(ENDPOINTS.recipes.bomFlat(id), quantity ? { quantity } : undefined),
    getFeasibility: (id: string, quantity?: number) =>
      api.get(ENDPOINTS.recipes.feasibility(id), quantity !== undefined ? { quantity } : undefined),
    getSubAssemblies: (id: string) =>
      api.get(ENDPOINTS.recipes.subAssemblies(id)),
    getSizeFamilies: (channel?: string) =>
      api.get<ApiEnvelope<RecipeSizeFamily[]> & { total: number }>(
        ENDPOINTS.recipes.sizeFamilies,
        channel ? { channel } : undefined
      ),
    getOptimizationSummary: (id: string) =>
      api.get(ENDPOINTS.recipes.optimization.summary(id)),
    getOptimizationWastage: (id: string, days?: number) =>
      api.get(ENDPOINTS.recipes.optimization.wastage(id), days ? { days } : undefined),
    getOptimizationBatchSize: (id: string) =>
      api.get(ENDPOINTS.recipes.optimization.batchSize(id)),
    getOptimizationCosts: (id: string) =>
      api.get(ENDPOINTS.recipes.optimization.costs(id)),
    getOptimizationScore: (id: string) =>
      api.get(ENDPOINTS.recipes.optimization.score(id)),
    applyWastageOptimization: (id: string, data: { componentUpdates: Array<{ componentId: string; suggestedWastage: number }> }) =>
      api.post(ENDPOINTS.recipes.optimization.applyWastage(id), data),
  },

  referenceImages: {
    list: (params?: { recipeId?: string }) =>
      api.get<ApiListEnvelope<ReferenceImageRecord>>('/api/v1/reference-images', params),
    get: (id: string) =>
      api.get<ApiEnvelope<ReferenceImageRecord>>(`/api/v1/reference-images/${id}`),
    upload: (input: ReferenceImageUploadInput) => {
      const form = new FormData();
      form.append('image', input.image);
      form.append('recipeId', input.recipeId);
      if (input.angle) form.append('angle', input.angle);
      if (input.features?.trim()) form.append('features', input.features.trim());
      return api.postForm<ApiEnvelope<ReferenceImageRecord>>('/api/v1/reference-images', form);
    },
    extractFeatures: (id: string) =>
      api.post<ApiEnvelope<ReferenceImageRecord> & { message?: string }>(`/api/v1/reference-images/${id}/features`),
    delete: (id: string) =>
      api.delete<{ success: boolean; message?: string }>(`/api/v1/reference-images/${id}`),
    file: (id: string) => api.getBlob(`/api/v1/reference-images/${id}/file`),
  },

  // Production
  production: {
    listBatches: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.production.batches, params),
    getBatch: (id: string) => api.get(ENDPOINTS.production.getBatch(id)),
    createBatch: (data: unknown) =>
      api.post(ENDPOINTS.production.createBatch, data),
    deleteBatch: (id: string) =>
      api.delete(ENDPOINTS.production.deleteBatch(id)),
    getTodaySummary: () => api.get(ENDPOINTS.production.todaySummary),
    getHistory: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.production.history, params),
    listBuildPlans: (params: { recipeId?: string; status?: BuildPlanStatus }) =>
      api.get(ENDPOINTS.production.buildPlans, params),
    approveBuildPlan: (data: {
      recipeId: string;
      quantity: number;
      builds: Array<{ recipeId: string; quantity: number }>;
    }) => api.post(ENDPOINTS.production.buildPlans, data),
    buildBuildPlan: (id: string) => api.post(ENDPOINTS.production.buildPlanBuild(id)),
    cancelBuildPlan: (id: string) => api.post(ENDPOINTS.production.buildPlanCancel(id)),
  },

  // Purchase Orders
  purchaseOrders: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.purchaseOrders.list, params),
    get: (id: string) => api.get(ENDPOINTS.purchaseOrders.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.purchaseOrders.create, data),
    update: (id: string, data: unknown) => purchaseOrderTransport.update(id, data),
    generateFromSelectedSuggestions: (suggestionIds: string[]) =>
      purchaseOrderTransport.generateFromSelectedSuggestions(suggestionIds),
    delete: (id: string) => api.delete(ENDPOINTS.purchaseOrders.delete(id)),
    send: (id: string) => api.post(ENDPOINTS.purchaseOrders.send(id)),
    receive: (id: string, data?: unknown) =>
      api.post(ENDPOINTS.purchaseOrders.receive(id), data),
    cancel: (id: string) => api.post(ENDPOINTS.purchaseOrders.cancel(id)),
    getConsolidation: () =>
      api.get(`${ENDPOINTS.purchaseOrders.base}/consolidation`),
    addLineItem: (id: string, data: unknown) =>
      api.post(`${ENDPOINTS.purchaseOrders.base}/${id}/line-items`, data),
    autoGenerate: (data: unknown) =>
      api.post(`${ENDPOINTS.purchaseOrders.base}/auto-generate`, data),
    getPdf: (id: string) => api.getBlob(`${ENDPOINTS.purchaseOrders.base}/${id}/pdf`),
    emailToVendor: (id: string, data?: unknown) =>
      api.post(`${ENDPOINTS.purchaseOrders.base}/${id}/email`, data),
  },

  // Vendors
  vendors: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.vendors.list, params),
    get: (id: string) => api.get(ENDPOINTS.vendors.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.vendors.create, data),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.vendors.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.vendors.delete(id)),
  },

  // QC
  qc: {
    inspections: {
      list: (params?: Record<string, unknown>) =>
        api.get('/api/v1/qc/inspections', params),
      get: (id: string) => api.get(`/api/v1/qc/inspections/${id}`),
      create: (data: unknown) => api.post('/api/v1/qc/inspections', data),
      pending: () => api.get('/api/v1/qc/inspections/pending'),
      start: (id: string, data?: unknown) =>
        api.post(`/api/v1/qc/inspections/${id}/start`, data),
      pass: (id: string, data: unknown) =>
        api.post(`/api/v1/qc/inspections/${id}/pass`, data),
      fail: (id: string, data: unknown) =>
        api.post(`/api/v1/qc/inspections/${id}/fail`, data),
    },
    templates: {
      list: (params?: Record<string, unknown>) =>
        api.get('/api/v1/qc/templates', params),
      create: (data: unknown) => api.post('/api/v1/qc/templates', data),
    },
    metrics: (params?: Record<string, unknown>) =>
      api.get('/api/v1/qc/metrics', params),
  },

  // Dashboard
  dashboard: {
    getStats: () => api.get(ENDPOINTS.dashboard.stats),
    getLowStock: () => api.get(ENDPOINTS.dashboard.lowStock),
    getRecentActivity: () => api.get(ENDPOINTS.dashboard.recentActivity),
    getForecasts: () => api.get(ENDPOINTS.dashboard.forecasts),
    getActionItems: () => api.get(ENDPOINTS.dashboard.actionItems),
  },

  // Reports
  reports: {
    getInventoryTrends: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.reports.inventoryTrends, params),
    getProductionVolume: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.reports.productionVolume, params),
    getConsumptionTrends: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.reports.consumptionTrends, params),
    getVendorPerformance: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.reports.vendorPerformance, params),
    getWastageAnalysis: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.reports.wastageAnalysis, params),
    getDaily: async <T>(date?: string): Promise<T> => {
      const response = await api.get<ApiEnvelope<T>>('/api/v1/reports/daily', date ? { date } : undefined);
      return unwrapApiEnvelope(response, 'Failed to fetch daily report');
    },
    getWeekly: async <T>(weekStart?: string): Promise<T> => {
      const response = await api.get<ApiEnvelope<T>>('/api/v1/reports/weekly', weekStart ? { weekStart } : undefined);
      return unwrapApiEnvelope(response, 'Failed to fetch weekly report');
    },
    getMonthly: async <T>(month?: string): Promise<T> => {
      const response = await api.get<ApiEnvelope<T>>('/api/v1/reports/monthly', month ? { month } : undefined);
      return unwrapApiEnvelope(response, 'Failed to fetch monthly report');
    },
    getAnomalies: async <T>(): Promise<T[]> => {
      const response = await api.get<ApiListEnvelope<T>>('/api/v1/reports/anomalies');
      return unwrapApiEnvelope(response, 'Failed to fetch report anomalies');
    },
    getPdf: (id: string) => api.getBlob(`/api/v1/reports/${id}/pdf`),
    email: (id: string, recipients: string[]) =>
      api.post(`/api/v1/reports/${id}/email`, { recipients }),
  },

  anomalies: {
    list: (params?: AnomalyListParams) =>
      api.get<ApiEnvelope<DetectedAnomaly[]> & { count: number }>(ENDPOINTS.anomalies.list, params),
    summary: () =>
      api.get<ApiEnvelope<AnomalySummary>>(ENDPOINTS.anomalies.summary),
    detect: () =>
      api.post<ApiEnvelope<{ detected: number; resolved: number }>>(ENDPOINTS.anomalies.detect),
    resolve: (id: string) =>
      api.post<ApiEnvelope<DetectedAnomaly>>(ENDPOINTS.anomalies.resolve(id)),
    exclude: (id: string) =>
      api.post<ApiEnvelope<DetectedAnomaly>>(ENDPOINTS.anomalies.exclude(id)),
  },

  // Scan
  scan: {
    barcode: (data: { barcode: string }) =>
      api.post('/api/v1/scan/barcode', data),
    barcodeProduce: (data: {
      barcode: string;
      recipeId?: string;
      quantityProduced?: number;
      isSpecialBatch?: boolean;
      scanSessionId?: string;
      previewToken?: string;
      componentOverrides?: Array<{
        materialId: string;
        overrideQty: number;
        unit: string;
      }>;
      confirm?: boolean;
    }) => api.post('/api/v1/scan/barcode/produce', data),
    manualLookup: (data: { query: string }) =>
      api.post('/api/v1/scan/manual-lookup', data),
    history: (params?: Record<string, unknown>) =>
      api.get('/api/v1/scan/history', params),
    stats: () => api.get('/api/v1/scan/stats'),
  },

  // AI
  ai: {
    status: () =>
      api.get('/api/v1/ai/status'),
    identify: (data: { image: string }) =>
      api.post('/api/v1/ai/identify', data),
  },

  // Predictions
  predictions: {
    getReorder: () => api.get(ENDPOINTS.predictions.reorder),
    getReorderForMaterial: (materialId: string) =>
      api.get(ENDPOINTS.predictions.reorderForMaterial(materialId)),
    getStockout: (params?: { withinDays?: number }) =>
      api.get(ENDPOINTS.predictions.stockout, params),
    getSuggestions: () => api.get(ENDPOINTS.predictions.suggestions),
    getAnalytics: (materialId: string, params?: { days?: number }) =>
      api.get(ENDPOINTS.predictions.analytics(materialId), params),
    getAnalyticsTrend: (materialId: string, params?: { period?: string }) =>
      api.get(ENDPOINTS.predictions.analyticsTrend(materialId), params),
    getReorderConfig: (materialId: string) =>
      api.get(ENDPOINTS.predictions.reorderConfig(materialId)),
    updateReorderConfig: (materialId: string, data: {
      safetyBufferDays?: number;
      reorderPointQuantityOverride?: number | null;
    }) =>
      api.put(ENDPOINTS.predictions.reorderConfig(materialId), data),
    getProjections: () => api.get(ENDPOINTS.predictions.projections),
  },

  // Import/Export
  importExport: {
    exportMaterials: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.importExport.exportMaterials, params),
    exportMaterialsBlob: (format: 'csv' | 'xlsx' | 'pdf') =>
      api.getBlob(ENDPOINTS.importExport.exportMaterials, { format }),
    exportItemsBlob: (format: 'csv' | 'xlsx' | 'pdf') =>
      api.getBlob(ENDPOINTS.importExport.exportItems, { format }),
    exportStockedValuationBlob: (format: 'csv' | 'xlsx' | 'pdf') =>
      api.getBlob(ENDPOINTS.importExport.exportStockedValuation, { format }),
    exportVendors: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.importExport.exportVendors, params),
    getInventoryTemplate: (format: 'xlsx' = 'xlsx') =>
      api.getBlob('/api/v1/import/templates/inventory', { format }),
  },

  // Import Jobs
  importJobs: {
    list: (params?: { page?: number; limit?: number }) =>
      api.get<ImportJobCollectionResponse>('/api/v1/import/jobs', params),
    create: (data: CreateImportJobRequest) =>
      api.post<ImportJobResponse>('/api/v1/import/jobs', data),
    preflight: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.postForm<ApiEnvelope<ImportPreflightResult>>(
        '/api/v1/import/jobs/preflight',
        form,
        { timeout: IMPORT_JOB_REQUEST_TIMEOUT_MS }
      );
    },
    convertWithAi: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.postForm<ApiEnvelope<ImportAiConversionResult>>(
        '/api/v1/import/jobs/convert/ai',
        form,
        { timeout: IMPORT_JOB_REQUEST_TIMEOUT_MS }
      );
    },
    get: (id: string) =>
      api.get<ImportJobResponse>(`/api/v1/import/jobs/${id}`),
    upload: (id: string, file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.postForm<ImportJobResponse>(
        `/api/v1/import/jobs/${id}/upload`,
        form,
        { timeout: IMPORT_JOB_REQUEST_TIMEOUT_MS }
      );
    },
    parse: (id: string) =>
      api.post<ImportJobResponse>(`/api/v1/import/jobs/${id}/parse`, undefined, {
        timeout: IMPORT_JOB_REQUEST_TIMEOUT_MS,
      }),
    updateRowAction: (id: string, rowId: string, selectedAction: ImportRowAction) =>
      api.patch<ImportJobResponse>(`/api/v1/import/jobs/${id}/rows/${rowId}/action`, {
        selectedAction,
      }),
    updateRowData: (id: string, rowId: string, normalizedData: Record<string, unknown>) =>
      api.patch<ImportJobResponse>(`/api/v1/import/jobs/${id}/rows/${rowId}/data`, {
        normalizedData,
      }),
    applyBulkConflictAction: (id: string, rowIds: string[], selectedAction: ImportRowAction) =>
      api.post<ApiEnvelope<ImportBulkConflictActionResult>>(`/api/v1/import/jobs/${id}/rows/conflicts/bulk-action`, {
        rowIds,
        selectedAction,
      }),
    commit: (id: string) =>
      api.post<ImportJobResponse>(`/api/v1/import/jobs/${id}/commit`, undefined, {
        timeout: IMPORT_JOB_REQUEST_TIMEOUT_MS,
      }),
    previewQuickBooks: (data: { mode: ImportJobMode }) =>
      api.post<ImportJobResponse>('/api/v1/import/jobs/quickbooks/preview', data),
    previewEcommerce: (data: { source: EcommerceImportSource; mode: ImportJobMode; connectionId?: string }) =>
      api.post<ImportJobResponse>('/api/v1/import/jobs/ecommerce/preview', data),
    previewProvider: (
      source: DirectImportPreviewProviderKey,
      data: { mode: ImportJobMode }
    ) => {
      const endpointBySource = {
        ZOHO_INVENTORY: '/api/v1/import/jobs/zoho-inventory/preview',
        LIGHTSPEED_INVENTORY: '/api/v1/import/jobs/lightspeed-inventory/preview',
        CIN7_INVENTORY: '/api/v1/import/jobs/cin7-inventory/preview',
        FISHBOWL_INVENTORY: '/api/v1/import/jobs/fishbowl-inventory/preview',
      } satisfies Record<DirectImportPreviewProviderKey, string>;
      return api.post<ImportJobResponse>(endpointBySource[source], data);
    },
    retry: (id: string) =>
      api.post<ImportJobResponse>(`/api/v1/import/jobs/${id}/retry`),
    cancel: (id: string) =>
      api.post<ImportJobResponse>(`/api/v1/import/jobs/${id}/cancel`),
    getErrorReport: (id: string) =>
      api.getBlob(`/api/v1/import/jobs/${id}/errors.csv`),
  },

  importProviders: {
    list: () => api.get<ApiEnvelope<ImportProviderMetadata[]>>('/api/v1/import/providers'),
  },

  // Auth
  auth: {
    login: (credentials: { email: string; password: string }) =>
      api.post(ENDPOINTS.auth.login, credentials),
    logout: () => api.post(ENDPOINTS.auth.logout),
    me: () => api.get(ENDPOINTS.auth.me),
    updateProfile: (data: { name?: string; email?: string }) =>
      api.patch(ENDPOINTS.auth.me, data),
    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      api.post(`${ENDPOINTS.auth.base}/change-password`, data),
    exportData: () => api.get(`${ENDPOINTS.auth.base}/export-data`),
    getPreferences: () => api.get(`${ENDPOINTS.auth.base}/preferences`),
    updatePreferences: (prefs: Record<string, boolean>) =>
      api.patch(`${ENDPOINTS.auth.base}/preferences`, prefs),
    deleteAccount: (password: string) =>
      api.post(`${ENDPOINTS.auth.base}/delete-account`, { password }),
    // Email verification
    verifyEmail: (token: string) =>
      api.post(ENDPOINTS.auth.verifyEmail, { token }),
    resendVerification: () =>
      api.post(ENDPOINTS.auth.resendVerification),
    requestPasswordReset: (email: string) =>
      api.post(ENDPOINTS.auth.forgotPassword, { email }),
    resetPassword: (token: string, newPassword: string) =>
      api.post(ENDPOINTS.auth.resetPassword, { token, newPassword }),
    // OTP login
    requestOtp: (email: string) =>
      api.post(ENDPOINTS.auth.otpRequest, { email }),
    verifyOtp: (email: string, code: string) =>
      api.post(ENDPOINTS.auth.otpVerify, { email, code }),
    // Magic link login
    requestMagicLink: (email: string) =>
      api.post(ENDPOINTS.auth.magicLinkRequest, { email }),
    verifyMagicLink: (token: string, rememberMe?: boolean) =>
      api.post(ENDPOINTS.auth.magicLinkVerify, { token, rememberMe }),
    // Passkeys
    passkeyAuthOptions: (email: string) =>
      api.post(ENDPOINTS.auth.passkeyAuthOptions, { email }),
    passkeyAuthVerify: (response: unknown) =>
      api.post(ENDPOINTS.auth.passkeyAuthVerify, { response }),
    passkeyRegisterOptions: () =>
      api.post(ENDPOINTS.auth.passkeyRegisterOptions),
    passkeyRegisterVerify: (response: unknown, deviceName?: string) =>
      api.post(ENDPOINTS.auth.passkeyRegisterVerify, { response, deviceName }),
    listPasskeys: () =>
      api.get(ENDPOINTS.auth.passkeys),
    deletePasskey: (id: string) =>
      api.delete(ENDPOINTS.auth.passkeyDelete(id)),
  },

  onboarding: {
    getStatus: () => api.get(ENDPOINTS.onboarding.status),
    updateStep: (step: number) => api.put(ENDPOINTS.onboarding.step, { step }),
    createOrganization: (data: { name: string; slug: string; industry?: string }) =>
      api.post(ENDPOINTS.onboarding.organization, data),
    complete: () => api.post(ENDPOINTS.onboarding.complete),
    skip: () => api.post(ENDPOINTS.onboarding.skip),
    reset: () => api.post(ENDPOINTS.onboarding.reset),
    invite: (emails: string[]) => api.post(ENDPOINTS.onboarding.invite, { emails }),
  },

  // Organization / Email Config
  organizations: {
    list: () => api.get(ENDPOINTS.organizations.list),
    create: (data: { name: string; slug: string; plan?: 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE' }) =>
      api.post(ENDPOINTS.organizations.create, data),
    getSizeVariants: () =>
      api.get<ApiEnvelope<SizeVariant[]>>(ENDPOINTS.organizations.sizeVariants),
    updateSizeVariants: (sizeVariants: SizeVariant[]) =>
      api.put<ApiEnvelope<SizeVariant[]>>(ENDPOINTS.organizations.sizeVariants, { sizeVariants }),
    getEmailConfig: () =>
      api.get(ENDPOINTS.organizations.emailConfig),
    updateEmailConfig: (data: {
      smtpHost: string;
      smtpPort: number;
      smtpSecure: boolean;
      smtpUser: string;
      smtpPass: string;
      fromAddress: string;
      fromName: string;
    }) => api.put(ENDPOINTS.organizations.emailConfig, data),
    testEmailConfig: () =>
      api.post(ENDPOINTS.organizations.testEmailConfig),
  },

  // Notifications
  notifications: {
    list: (params?: { page?: number; limit?: number }) =>
      api.get(ENDPOINTS.notifications.list, params),
    unreadCount: () =>
      api.get(ENDPOINTS.notifications.unreadCount),
    markRead: (id: string) =>
      api.patch(ENDPOINTS.notifications.markRead(id)),
    markAllRead: () =>
      api.patch(ENDPOINTS.notifications.markAllRead),
  },

  // Lots
  lots: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.lots.list, params),
    get: (id: string) => api.get(ENDPOINTS.lots.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.lots.create, data),
    update: (id: string, data: unknown) =>
      api.put(ENDPOINTS.lots.update(id), data),
    retire: (id: string) => api.post(ENDPOINTS.lots.retire(id)),
    expiryAlerts: (params?: { criticalDays?: number; warningDays?: number }) =>
      api.get('/api/v1/lots/alerts', params),
    expiryStats: () =>
      api.get('/api/v1/lots/expiry-stats'),
    expiryCalendar: (params?: { startDate?: string; endDate?: string }) =>
      api.get('/api/v1/lots/expiry-calendar', params),
    expiring: (params?: { days?: number; materialId?: string }) =>
      api.get('/api/v1/lots/expiring', params),
  },

  // Cycle Counts
  cycleCounts: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.cycleCounts.list, params),
    get: (id: string, params?: Record<string, unknown>) => api.get(ENDPOINTS.cycleCounts.get(id), params),
    create: (data: unknown) => api.post(ENDPOINTS.cycleCounts.create, data),
    start: (id: string) => api.post(ENDPOINTS.cycleCounts.start(id)),
    submit: (id: string) => api.post(ENDPOINTS.cycleCounts.submit(id)),
    approve: (id: string) => api.post(ENDPOINTS.cycleCounts.approve(id)),
    apply: (id: string) => api.post(ENDPOINTS.cycleCounts.apply(id)),
    cancel: (id: string) => api.post(ENDPOINTS.cycleCounts.cancel(id)),
    accuracy: () => api.get(ENDPOINTS.cycleCounts.accuracy),
    abcSummary: () => api.get(ENDPOINTS.cycleCounts.abcSummary),
    abcCalculate: () => api.post(ENDPOINTS.cycleCounts.abcCalculate),
    abcDue: () => api.get(ENDPOINTS.cycleCounts.abcDue),
  },

  // Serials
  serials: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.serials.list, params),
    get: (id: string) => api.get(ENDPOINTS.serials.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.serials.create, data),
    bulkCreate: (data: unknown) => api.post(ENDPOINTS.serials.bulkCreate, data),
    lookup: (serialNumber: string) => api.get(ENDPOINTS.serials.lookup(serialNumber)),
    history: (id: string) => api.get(ENDPOINTS.serials.history(id)),
    allocate: (id: string, data?: unknown) => api.post(ENDPOINTS.serials.allocate(id), data),
    deallocate: (id: string) => api.post(ENDPOINTS.serials.deallocate(id)),
    sell: (id: string, data?: unknown) => api.post(ENDPOINTS.serials.sell(id), data),
    scrap: (id: string, data?: unknown) => api.post(ENDPOINTS.serials.scrap(id), data),
  },

  // Warehouses
  warehouses: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.warehouse.list, params),
    get: (id: string) => api.get(ENDPOINTS.warehouse.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.warehouse.create, data),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.warehouse.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.warehouse.delete(id)),
    setDefault: (id: string) => api.post(ENDPOINTS.warehouse.setDefault(id)),
    getLocations: (id: string) => api.get(ENDPOINTS.warehouse.getLocations(id)),
    createLocation: (id: string, data: unknown) =>
      api.post(ENDPOINTS.warehouse.createLocation(id), data),
    updateLocation: (id: string, locationId: string, data: unknown) =>
      api.patch(ENDPOINTS.warehouse.updateLocation(id, locationId), data),
    deleteLocation: (id: string, locationId: string) =>
      api.delete(ENDPOINTS.warehouse.deleteLocation(id, locationId)),
    getInventory: (id: string) => api.get(ENDPOINTS.warehouse.getInventory(id)),
  },

  // Transfers
  transfers: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.transfers.list, params),
    get: (id: string) => api.get(ENDPOINTS.transfers.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.transfers.create, data),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.transfers.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.transfers.delete(id)),
    submit: (id: string) => api.post(ENDPOINTS.transfers.submit(id)),
    ship: (id: string, data: unknown) => api.post(ENDPOINTS.transfers.ship(id), data),
    receive: (id: string, data: unknown) => api.post(ENDPOINTS.transfers.receive(id), data),
    cancel: (id: string) => api.post(ENDPOINTS.transfers.cancel(id)),
    pending: () => api.get(ENDPOINTS.transfers.pending),
    statistics: () => api.get(ENDPOINTS.transfers.statistics),
  },

  // Vendor Intelligence
  vendorIntelligence: {
    getScore: (vendorId: string) =>
      api.get(ENDPOINTS.vendorIntelligence.score(vendorId)),
    getLeadTime: (vendorId: string, params: { materialId: string; orderDate?: string }) =>
      api.get(ENDPOINTS.vendorIntelligence.leadTime(vendorId), params),
    getConsolidation: () =>
      api.get(ENDPOINTS.vendorIntelligence.consolidation),
    compareVendors: (materialId: string) =>
      api.get(ENDPOINTS.vendorIntelligence.compare(materialId)),
    getRankings: () =>
      api.get(ENDPOINTS.vendorIntelligence.rankings),
    getInsights: () =>
      api.get(ENDPOINTS.vendorIntelligence.insights),
  },

  // RMA (Return Merchandise Authorization)
  rma: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.rma.list, params),
    get: (id: string) => api.get(ENDPOINTS.rma.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.rma.create, data),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.rma.update(id), data),
    approve: (id: string) => api.post(ENDPOINTS.rma.approve(id)),
    inspect: (id: string, data?: unknown) =>
      api.post(ENDPOINTS.rma.inspect(id), data),
    resolve: (id: string, data: unknown) =>
      api.post(ENDPOINTS.rma.resolve(id), data),
    cancel: (id: string) => api.post(ENDPOINTS.rma.cancel(id)),
    stats: () => api.get(ENDPOINTS.rma.stats),
  },

  // Packing / Containers
  packing: {
    list: (params?: Record<string, unknown>) => api.get(ENDPOINTS.packing.list, params),
    get: (id: string) => api.get(ENDPOINTS.packing.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.packing.create, data),
    update: (id: string, data: unknown) => api.patch(ENDPOINTS.packing.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.packing.delete(id)),
    addItem: (id: string, data: unknown) => api.post(ENDPOINTS.packing.addItem(id), data),
    removeItem: (id: string, itemId: string) => api.delete(ENDPOINTS.packing.removeItem(id, itemId)),
    pack: (id: string) => api.post<ApiEnvelope<FulfillmentStepResult>>(ENDPOINTS.packing.pack(id)),
    addPhoto: (id: string, data: unknown) => api.post(`${ENDPOINTS.packing.get(id)}/photos`, data),
    listPhotos: (id: string) => api.get(`${ENDPOINTS.packing.get(id)}/photos`),
    seal: (id: string, data?: unknown) => api.post(ENDPOINTS.packing.seal(id), data),
    ship: (id: string) => api.post<ApiEnvelope<FulfillmentStepResult>>(ENDPOINTS.packing.ship(id)),
    packingList: (id: string) => api.get(ENDPOINTS.packing.packingList(id)),
    packStation: (id: string) => api.get(ENDPOINTS.packing.packStation(id)),
    scan: (id: string, data: { code: string }) => api.post(ENDPOINTS.packing.scan(id), data),
    stats: () => api.get(ENDPOINTS.packing.stats),
  },

  // Cartonization / Box Catalog
  cartonization: {
    listBoxes: (params?: Record<string, unknown>) =>
      api.get('/api/v1/cartonization/boxes', params),
    createBox: (data: unknown) => api.post('/api/v1/cartonization/boxes', data),
    updateBox: (id: string, data: unknown) =>
      api.patch(`/api/v1/cartonization/boxes/${id}`, data),
    deactivateBox: (id: string) => api.delete(`/api/v1/cartonization/boxes/${id}`),
    seedDefaultBoxes: () => api.post('/api/v1/cartonization/boxes/seed-defaults'),
    recommend: (data: unknown) => api.post('/api/v1/cartonization/recommend', data),
    recordBoxUsage: (data: unknown) => api.patch('/api/v1/cartonization/box-usage', data),
  },

  // Subcontracting
  subcontracting: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.subcontracting.list, params),
    get: (id: string) => api.get(ENDPOINTS.subcontracting.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.subcontracting.create, data),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.subcontracting.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.subcontracting.delete(id)),
    send: (id: string) => api.post(ENDPOINTS.subcontracting.send(id)),
    acknowledge: (id: string) => api.post(ENDPOINTS.subcontracting.acknowledge(id)),
    start: (id: string) => api.post(ENDPOINTS.subcontracting.start(id)),
    qualityCheck: (id: string) => api.post(ENDPOINTS.subcontracting.qualityCheck(id)),
    complete: (id: string, data: unknown) =>
      api.post(ENDPOINTS.subcontracting.complete(id), data),
    cancel: (id: string) => api.post(ENDPOINTS.subcontracting.cancel(id)),
    addItem: (id: string, data: unknown) =>
      api.post(ENDPOINTS.subcontracting.addItem(id), data),
    removeItem: (id: string, itemId: string) =>
      api.delete(ENDPOINTS.subcontracting.removeItem(id, itemId)),
    stats: () => api.get(ENDPOINTS.subcontracting.stats),
  },

  // Manufacturing Orders
  manufacturingOrders: {
    list: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.manufacturingOrders.list, params),
    get: (id: string) => api.get(ENDPOINTS.manufacturingOrders.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.manufacturingOrders.create, data),
    createRun: (data: unknown) => api.post(ENDPOINTS.manufacturingOrders.createRun, data),
    getRun: (id: string) => api.get(ENDPOINTS.manufacturingOrders.getRun(id)),
    update: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.manufacturingOrders.update(id), data),
    schedule: (id: string, data?: { scheduledStart?: string }) =>
      api.post(ENDPOINTS.manufacturingOrders.schedule(id), data),
    start: (id: string) => api.post(ENDPOINTS.manufacturingOrders.start(id)),
    complete: (id: string) => api.post(ENDPOINTS.manufacturingOrders.complete(id)),
    close: (id: string) => api.post(ENDPOINTS.manufacturingOrders.close(id)),
    cancel: (id: string) => api.post(ENDPOINTS.manufacturingOrders.cancel(id)),
    stats: () => api.get(ENDPOINTS.manufacturingOrders.stats),
  },

  // Labor Tracking
  labor: {
    clockIn: (data: unknown) => api.post(ENDPOINTS.labor.clockIn, data),
    clockOut: (id: string) => api.post(ENDPOINTS.labor.clockOut(id)),
    addBreak: (id: string, data: { minutes: number }) =>
      api.post(ENDPOINTS.labor.addBreak(id), data),
    manual: (data: unknown) => api.post(ENDPOINTS.labor.manual, data),
    mine: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.labor.mine, params),
    summary: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.labor.summary, params),
    byMO: (moId: string) => api.get(ENDPOINTS.labor.byMO(moId)),
    byBatch: (batchId: string) => api.get(ENDPOINTS.labor.byBatch(batchId)),
    delete: (id: string) => api.delete(ENDPOINTS.labor.delete(id)),
  },

  // Approvals
  approvals: {
    listWorkflows: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.approvals.workflows, params),
    getWorkflow: (id: string) => api.get(ENDPOINTS.approvals.getWorkflow(id)),
    createWorkflow: (data: unknown) => api.post(ENDPOINTS.approvals.createWorkflow, data),
    updateWorkflow: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.approvals.updateWorkflow(id), data),
    deleteWorkflow: (id: string) => api.delete(ENDPOINTS.approvals.deleteWorkflow(id)),
    submit: (data: unknown) => api.post(ENDPOINTS.approvals.submit, data),
    pending: () => api.get(ENDPOINTS.approvals.pending),
    getRecord: (id: string) => api.get(ENDPOINTS.approvals.getRecord(id)),
    decide: (id: string, data: unknown) => api.post(ENDPOINTS.approvals.decide(id), data),
    cancelRecord: (id: string) => api.post(ENDPOINTS.approvals.cancelRecord(id)),
    entityRecords: (entityType: string, entityId: string) =>
      api.get(ENDPOINTS.approvals.entityRecords(entityType, entityId)),
  },

  // Kits / Bundles
  kits: {
    list: (params?: Record<string, unknown>) => api.get(ENDPOINTS.kits.list, params),
    get: (id: string) => api.get(ENDPOINTS.kits.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.kits.create, data),
    update: (id: string, data: unknown) => api.patch(ENDPOINTS.kits.update(id), data),
    delete: (id: string) => api.delete(ENDPOINTS.kits.delete(id)),
    assemble: (id: string, data: { quantity: number }) => api.post(ENDPOINTS.kits.assemble(id), data),
    feasibility: (id: string) => api.get(ENDPOINTS.kits.feasibility(id)),
    importWorkbook: (
      file: File,
      options?: { dryRun?: boolean; skipUnresolved?: boolean }
    ) => {
      const form = new FormData();
      form.append('file', file);
      const dryRun = options?.dryRun ?? true;
      const skipUnresolved = options?.skipUnresolved === true;
      const query = new URLSearchParams({
        dryRun: dryRun ? 'true' : 'false',
        skipUnresolved: skipUnresolved ? 'true' : 'false',
      });
      return api.postForm<ApiEnvelope<KitImportResult>>(
        `${ENDPOINTS.kits.import}?${query.toString()}`,
        form,
        { timeout: 120000 }
      );
    },
  },

  // Quotes
  quotes: {
    list: (params?: Record<string, unknown>) => api.get(ENDPOINTS.quotes.list, params),
    get: (id: string) => api.get(ENDPOINTS.quotes.get(id)),
    create: (data: unknown) => api.post(ENDPOINTS.quotes.create, data),
    update: (id: string, data: unknown) => api.patch(ENDPOINTS.quotes.update(id), data),
    submitForApproval: (id: string) => api.post(ENDPOINTS.quotes.submitForApproval(id)),
    refreshApproval: (id: string) => api.post(ENDPOINTS.quotes.refreshApproval(id)),
    getPdf: (id: string) => api.getBlob(ENDPOINTS.quotes.pdf(id)),
    send: (id: string, data?: unknown) => api.post(ENDPOINTS.quotes.send(id), data),
    accept: (id: string) => api.post(ENDPOINTS.quotes.accept(id)),
    decline: (id: string) => api.post(ENDPOINTS.quotes.decline(id)),
  },

  // Custom Fields
  customFields: {
    listDefinitions: (params?: Record<string, unknown>) => api.get(ENDPOINTS.customFields.definitions, params),
    createDefinition: (data: unknown) => api.post(ENDPOINTS.customFields.definitions, data),
    updateDefinition: (id: string, data: unknown) => api.patch(ENDPOINTS.customFields.updateDefinition(id), data),
    deleteDefinition: (id: string) => api.delete(ENDPOINTS.customFields.deleteDefinition(id)),
    getValues: (entityType: string, entityId: string) => api.get(ENDPOINTS.customFields.getValues(entityType, entityId)),
    setValues: (entityType: string, entityId: string, data: unknown) => api.put(ENDPOINTS.customFields.setValues(entityType, entityId), data),
    deleteValue: (entityType: string, entityId: string, fieldKey: string) => api.delete(ENDPOINTS.customFields.deleteValue(entityType, entityId, fieldKey)),
  },

  // Chat / NL Query
  chat: {
    query: (data: { message: string; sessionId?: string }) =>
      api.post(ENDPOINTS.chat.query, data),
    getSessions: () =>
      api.get(ENDPOINTS.chat.sessions),
    getSession: (sessionId: string) =>
      api.get(ENDPOINTS.chat.session(sessionId)),
    deleteSession: (sessionId: string) =>
      api.delete(ENDPOINTS.chat.session(sessionId)),
    getSuggestions: () =>
      api.get(ENDPOINTS.chat.suggestions),
  },

  // Capacity Planning
  capacity: {
    listResources: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.capacity.resources, params),
    getResource: (id: string) => api.get(ENDPOINTS.capacity.getResource(id)),
    createResource: (data: unknown) =>
      api.post(ENDPOINTS.capacity.createResource, data),
    updateResource: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.capacity.updateResource(id), data),
    deleteResource: (id: string) =>
      api.delete(ENDPOINTS.capacity.deleteResource(id)),
    scheduleResource: (id: string, data: unknown) =>
      api.post(ENDPOINTS.capacity.scheduleResource(id), data),
    getSchedule: (id: string, params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.capacity.getSchedule(id), params),
    removeSchedule: (id: string) =>
      api.delete(ENDPOINTS.capacity.removeSchedule(id)),
    getOverview: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.capacity.overview, params),
    getBottlenecks: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.capacity.bottlenecks, params),
    getAvailability: (id: string, params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.capacity.availability(id), params),
  },

  // Master Production Schedule (MPS)
  mps: {
    listPlans: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.mps.plans, params),
    createPlan: (data: unknown) => api.post(ENDPOINTS.mps.createPlan, data),
    getPlan: (id: string) => api.get(ENDPOINTS.mps.getPlan(id)),
    updatePlan: (id: string, data: unknown) =>
      api.patch(ENDPOINTS.mps.updatePlan(id), data),
    deletePlan: (id: string) => api.delete(ENDPOINTS.mps.deletePlan(id)),
    publish: (id: string) => api.post(ENDPOINTS.mps.publish(id)),
    activate: (id: string) => api.post(ENDPOINTS.mps.activate(id)),
    complete: (id: string) => api.post(ENDPOINTS.mps.complete(id)),
    archive: (id: string) => api.post(ENDPOINTS.mps.archive(id)),
    addItem: (planId: string, data: unknown) =>
      api.post(ENDPOINTS.mps.items(planId), data),
    getItems: (planId: string) => api.get(ENDPOINTS.mps.items(planId)),
    updateItem: (itemId: string, data: unknown) =>
      api.patch(ENDPOINTS.mps.updateItem(itemId), data),
    deleteItem: (itemId: string) =>
      api.delete(ENDPOINTS.mps.deleteItem(itemId)),
    getMaterialRequirements: (planId: string) =>
      api.get(ENDPOINTS.mps.materialRequirements(planId)),
    generateMOs: (planId: string) =>
      api.post(ENDPOINTS.mps.generateMOs(planId)),
    getDemand: (params?: Record<string, unknown>) =>
      api.get(ENDPOINTS.mps.demand, params),
    setDemand: (data: unknown) => api.post(ENDPOINTS.mps.setDemand, data),
    generateDemand: (data: unknown) =>
      api.post(ENDPOINTS.mps.generateDemand, data),
  },
};
